import { FC, useCallback, useMemo } from "react";
import { t } from "i18next";
import { useParams } from "react-router-dom";
import { Formik, FormikHelpers } from "formik";

import { ButtonPrimary } from "@/shared/components/atoms/ButtonPrimary";
import { InputFilepdf } from "@/shared/components/atoms/InputFilePDF";
import { toasts } from "@/shared/utils/toasts";
import {
  contratosInititalValues,
  contratosValidationSchema,
} from "@/shared/schemas/settings.schema";
import { useContractsClausesMutation } from "@/app/settings/contracts-clauses.api";

import { DOMAIN } from "@/app/app.api";
import { useGetBranchQuery } from "@/app/branchs/branchs.api";

export const ContratosClausulas: FC<any> = () => {
  const { id } = useParams();

  const getBranchQuery = useGetBranchQuery({
    branchId: id,
  });

  const [updateContractClauses] = useContractsClausesMutation();

  const handleSubmit = useCallback(
    async (values, helpers: FormikHelpers<any>) => {
      try {
        await updateContractClauses({
          contract: values.contract,
          clauses: values.clauses,
          branchId: id,
        }).unwrap();

        getBranchQuery.refetch();
        helpers.resetForm();
        toasts.success("Exito", "Documentos actualizados exitosamente");
      } catch (error) {
        console.error(error);
        toasts.danger("Error", "Ocurrió un error al actualizar los documentos");
      }
    },
    [id]
  );

  const { contractFileUrl, clausesFileUrl } = useMemo(() => {
    const contractFile = getBranchQuery.data?.contract_user;
    const clausesFile = getBranchQuery.data?.clauses_user;

    return {
      contractFileUrl: contractFile ? `${DOMAIN}/${contractFile}` : undefined,
      clausesFileUrl: clausesFile ? `${DOMAIN}/${clausesFile}` : undefined,
    };
  }, [getBranchQuery.data]);

  return (
    <div className="flex flex-col items-center h-full w-full mt-8">
      <div className="w-full sm:max-w-full md:max-w-full lg:max-w-full xl:max-w-full 2xl:max-w-full mx-auto mb-8">
        <div className="w-full flex justify-start px-4 mb-4">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {t("share.views.contratos_clausulas_clientes")}
          </h1>
        </div>

        <div className="border-b border-gray-300 my-3"></div>

        <div className="flex flex-col md:flex-row">
          {/* Formulario de carga de archivos en la parte derecha */}
          <div className="flex flex-col w-full md:w-full p-4">
            <Formik
              enableReinitialize
              initialValues={contratosInititalValues}
              validationSchema={contratosValidationSchema}
              onSubmit={handleSubmit}
            >
              {({ handleSubmit, errors, values, setFieldValue }) => (
                <form
                  onSubmit={handleSubmit}
                  className="w-full flex flex-col items-center justify-center"
                >
                  <div className="w-full flex flex-col md:flex-row items-center justify-center md:space-x-20 p-4 gap-8">
                    <div className="flex flex-col items-center gap-2 h-[260px]">
                      <InputFilepdf
                        name="contrato"
                        id="contrato"
                        label={t("share.views.Subir_Reemplazar_contrato")}
                        span=""
                        spanButton={t("share.views.up_contracto")}
                        description={t("general.format_pdf")}
                        selectedFileName={values.contract?.name || ""}
                        error={!!errors.contract}
                        onFileChange={(file) => {
                          setFieldValue("contract", file);
                        }}
                        onRemoveFile={() => {
                          setFieldValue("contract", null);
                        }}
                        tittleDelete={t("general.delete_archive")}
                      />
                      {contractFileUrl && (
                        <a
                          href={contractFileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline"
                        >
                          Ver contrato
                        </a>
                      )}
                    </div>
                    <div className="flex flex-col items-center gap-2 h-[260px]">
                      <InputFilepdf
                        name="clauses"
                        id="clauses"
                        label={t("share.views.Subir_Reemplazar_cláusulas")}
                        span=""
                        spanButton={t("share.views.up_cláusulas")}
                        description={t("general.format_pdf")}
                        selectedFileName={values.clauses?.name || ""}
                        error={!!errors.clauses}
                        onFileChange={(file) => {
                          setFieldValue("clauses", file);
                        }}
                        onRemoveFile={() => {
                          setFieldValue("clauses", null);
                        }}
                        tittleDelete={t("general.delete_archive")}
                      />
                      {clausesFileUrl && (
                        <a
                          href={clausesFileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline"
                        >
                          Ver cláusulas
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Botón centrado */}
                  <div className="mt-5 flex justify-center w-full">
                    <ButtonPrimary
                      className="mt-5"
                      text="Guardar"
                      size="large"
                      type="submit"
                      disabled={!values.contract && !values.clauses}
                    />
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};
